import { useMemo } from 'react';

import usePagination from '@/hooks/usePagination';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { GroupAdditionMethodEnum, OrderEnum } from '@/types/schema';

import { useUsersQuery } from '../graphql/queries/generated/Users';
import { UseUsersType } from '../types';

function useUsers({
  searchText,
  ids,
  emailIds,
  tagIds,
  pause = false,
  first,
  groupIds,
  userTypes,
  staffRoleIds,
  orgRoleIds,
  status,
  crossSubWorkspace,
  excludeSelf = false,
  sort,
  OR,
  currentUserId,
  filterGroup,
  trackIds,
  isInGroupCheckId = '',
  userGroupIds,
  notInIds,
  orgRoleId = '',
  ...restFilters
}: UseUsersType) {
  const user = useUserContext();
  const {
    isLoading,
    isFetchingMore,
    error,
    data: allUsers,
    refetch,
    fetchMore,
    isFetching,
    totalCount,
  } = usePagination(
    useUsersQuery,
    {
      filters: {
        searchText,
        ids,
        emailIds,
        tagIds,
        userTypes,
        staffRoleIds,
        orgRoleIds,
        status,
        crossSubWorkspace,
        trackIds,
        filterGroup,
        notInIds,
        groupFilter:
          groupIds && groupIds.length
            ? {
                groupIds,
                groupAdditionMethod: GroupAdditionMethodEnum.All,
              }
            : null,
        OR,
        ...restFilters,
      },
      sort: sort ?? [
        {
          field: 'name',
          order: OrderEnum.Asc,
        },
      ],
      first,
      userId: currentUserId,
      groupIdPresent: Boolean(isInGroupCheckId.length),
      groupId: isInGroupCheckId,
      isgroupIdsPresent: !!userGroupIds?.length,
      groupIds: userGroupIds ?? [],
      orgRoleId,
      isOrgRoleIdPresent: !!orgRoleId,
    },
    {
      edgeKey: 'users',
      pause,
    },
  );

  const users = useMemo(() => {
    const newAllUsers = OR ? allUsers.filter((userItem) => userItem.canBeNominated) : allUsers;
    return excludeSelf ? newAllUsers.filter((userItem) => userItem.id !== user.id) : newAllUsers;
  }, [allUsers, excludeSelf, user.id, OR, userGroupIds]);

  const resolvedTotalCount =
    excludeSelf && totalCount && totalCount > 0 ? totalCount - 1 : totalCount;

  return {
    isLoading,
    isFetching,
    isFetchingMore,
    error,
    allUsers: allUsers,
    data: users,
    refetch,
    fetchMore,
    totalCount: resolvedTotalCount,
  };
}

export default useUsers;

export type UseUsersDataType = ReturnType<typeof useUsers>['data'][0];
